import { useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { EyeIcon, EyeOffIcon } from 'lucide-react';
import { getApiErrorMessage, getApiFieldErrors, isApiError } from '@noor/api';
import { toast } from '@noor/lib';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  FormField,
} from '@noor/ui';
import { changePasswordFormSchema, type ChangePasswordFormValues } from '../model/schema';
import { useChangePassword } from '../model/use-change-password';

interface ChangePasswordDialogProps {
  open: boolean;
  onClose: () => void;
}

const VALIDATION_CODE = '5000';

function localizePasswordFieldError(
  field: 'old_password' | 'new_password',
  message: string,
  t: (key: string) => string,
): string {
  const normalized = message.toLowerCase();
  if (field === 'old_password') {
    if (
      normalized.includes('required') ||
      normalized.includes('empty') ||
      normalized.includes('пуст') ||
      normalized.includes('обяз')
    ) {
      return t('auth.passwordRequired');
    }
    return t('auth.oldPasswordMismatch');
  }
  if (normalized.includes('128') || normalized.includes('max')) {
    return t('auth.passwordMaxLength');
  }
  if (normalized.includes('8') || normalized.includes('min')) {
    return t('auth.passwordMinLength');
  }
  if (
    normalized.includes('letter') ||
    normalized.includes('digit') ||
    normalized.includes('букв') ||
    normalized.includes('цифр')
  ) {
    return t('auth.passwordLettersAndDigits');
  }
  if (
    normalized.includes('required') ||
    normalized.includes('empty') ||
    normalized.includes('пуст') ||
    normalized.includes('обяз')
  ) {
    return t('auth.passwordRequired');
  }
  return t('auth.passwordValidation');
}

/** Reveal/hide control shared by the three password fields. */
function VisibilityToggle({
  visible,
  onToggle,
  label,
}: {
  visible: boolean;
  onToggle: () => void;
  label: string;
}) {
  const Icon = visible ? EyeOffIcon : EyeIcon;
  return (
    <button
      type="button"
      onClick={onToggle}
      tabIndex={-1}
      aria-label={label}
      aria-pressed={visible}
      className="flex cursor-pointer items-center rounded-full text-muted-foreground transition-colors outline-none hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50"
    >
      <Icon className="size-5" aria-hidden="true" />
    </button>
  );
}

function PasswordFormField({
  name,
  label,
  placeholder,
  autoComplete,
  disabled,
  showLabel,
  hideLabel,
}: {
  name: 'old_password' | 'new_password' | 'confirm_password';
  label: string;
  placeholder: string;
  autoComplete: string;
  disabled: boolean;
  showLabel: string;
  hideLabel: string;
}) {
  const [visible, setVisible] = useState(false);
  return (
    <FormField name={name}>
      <FormField.Label>{label}</FormField.Label>
      <FormField.Input
        type={visible ? 'text' : 'password'}
        autoComplete={autoComplete}
        placeholder={placeholder}
        disabled={disabled}
        endIcon={
          <VisibilityToggle
            visible={visible}
            onToggle={() => setVisible((shown) => !shown)}
            label={visible ? hideLabel : showLabel}
          />
        }
      />
      <FormField.Message />
    </FormField>
  );
}

/**
 * Change-password dialog: old password, new password, confirm new password.
 * The new/confirm match is validated on the front (zod refine); the request
 * sends only `{old_password, new_password}` to the auth service.
 */
export function ChangePasswordDialog({ open, onClose }: ChangePasswordDialogProps) {
  const { t } = useTranslation('common');
  // `t` is stable; the schema rebuilds if the bound translator ever changes.
  const schema = useMemo(() => changePasswordFormSchema((key) => t(key)), [t]);
  const form = useForm<ChangePasswordFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { old_password: '', new_password: '', confirm_password: '' },
  });
  const changePassword = useChangePassword();
  const [serverError, setServerError] = useState<string | null>(null);

  if (!open) return null;

  const submit = (values: ChangePasswordFormValues) => {
    if (changePassword.isPending) return;
    setServerError(null);
    changePassword.mutate(
      { old_password: values.old_password, new_password: values.new_password },
      {
        onSuccess: () => {
          toast.success(t('auth.changePasswordSuccess'));
          form.reset();
          onClose();
        },
        onError: (err) => {
          const fields = getApiFieldErrors(err);
          let mapped = false;
          for (const name of ['old_password', 'new_password'] as const) {
            const message = fields[name];
            if (typeof message === 'string' && message.trim() !== '') {
              form.setError(name, {
                type: 'server',
                message:
                  isApiError(err) && err.code === VALIDATION_CODE
                    ? localizePasswordFieldError(name, message, t)
                    : message,
              });
              mapped = true;
            }
          }
          if (!mapped)
            setServerError(getApiErrorMessage(err, t('auth.changePasswordError')));
        },
      },
    );
  };

  const pending = changePassword.isPending;

  return (
    <Dialog open onOpenChange={(next) => !next && !pending && onClose()}>
      <DialogContent
        aria-describedby={undefined}
        className="flex max-h-[90vh] w-full max-w-[calc(100%-2rem)] flex-col overflow-hidden rounded-3xl p-8 sm:max-w-lg"
      >
        <DialogHeader className="shrink-0">
          <DialogTitle className="text-[16px] font-semibold tracking-[-0.64px] text-gray-800 dark:text-foreground">
            {t('auth.changePasswordTitle')}
          </DialogTitle>
        </DialogHeader>

        <FormProvider {...form}>
          <form
            id="change-password-form"
            onSubmit={form.handleSubmit(submit)}
            autoComplete="off"
            className="flex min-w-0 flex-col gap-[var(--form-gap-y)] overflow-y-auto px-1 py-4"
            noValidate
          >
            <PasswordFormField
              name="old_password"
              label={t('auth.oldPassword')}
              placeholder={t('auth.oldPasswordPlaceholder')}
              autoComplete="one-time-code"
              disabled={pending}
              showLabel={t('auth.showPassword')}
              hideLabel={t('auth.hidePassword')}
            />
            <PasswordFormField
              name="new_password"
              label={t('auth.newPassword')}
              placeholder={t('auth.newPasswordPlaceholder')}
              autoComplete="one-time-code"
              disabled={pending}
              showLabel={t('auth.showPassword')}
              hideLabel={t('auth.hidePassword')}
            />
            <PasswordFormField
              name="confirm_password"
              label={t('auth.confirmNewPassword')}
              placeholder={t('auth.confirmNewPasswordPlaceholder')}
              autoComplete="one-time-code"
              disabled={pending}
              showLabel={t('auth.showPassword')}
              hideLabel={t('auth.hidePassword')}
            />

            {serverError && (
              <p className="text-sm text-destructive" role="alert">
                {serverError}
              </p>
            )}
          </form>
        </FormProvider>

        <div className="flex shrink-0 items-center justify-end gap-3 border-t border-border pt-4">
          <Button
            type="button"
            variant="soft"
            size="modal"
            shape="rounded"
            onClick={onClose}
            disabled={pending}
          >
            {t('action.cancel')}
          </Button>
          <Button
            form="change-password-form"
            type="submit"
            variant="dark"
            size="modal"
            shape="rounded"
            disabled={pending}
          >
            {pending ? t('auth.changePasswordSaving') : t('action.save')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
